import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";

import { CheckCircle, ContentCopy } from "@mui/icons-material";
import knowledgebaseAgentService from "../../../services/knowledgebase-agent.service";

const WidgetCodeStep = forwardRef(
  ({ formData, updateFormData, onboardingData }, ref) => {
    const [widgetCode, setWidgetCode] = useState(formData.widgetCode || "");
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(!!formData.id);
    const [error, setError] = useState(null);

    useImperativeHandle(ref, () => ({
      getWidgetCode: () => widgetCode,
    }));

    /**
     * Load widget on edit mode only once
     */
    useEffect(() => {
      if (!formData.id) {
        setLoading(false);
        return;
      }

      // If widget already exists in formData → do not reload
      if (formData.widgetCode) {
        setWidgetCode(formData.widgetCode);
        setLoading(false);
        return;
      }

      const loadWidget = async () => {
        try {
          setLoading(true);

          const agentData =
            await knowledgebaseAgentService.getKnowledgeBaseById(formData.id);

          console.log("agentData ---->", agentData);

          // Correct validation
          if (!agentData) {
            setError("Agent not found.");
            return;
          }

          // if (agentData.crawlStatus !== "completed") {
          //   setError("Crawling not completed. Please try again later.");
          //   return;
          // }

          // Generate widget code
          const code = `
<script src="https://onboarding.sitepilot.online/widget.js" defer></script>
<script>
  window.addEventListener("load", function () {
    if (typeof window.initChatWidget === "function") {          
      window.initChatWidget({ kbId: "${formData.id}" });
    } else {
      console.error("Chat widget not loaded.");
    }
  });
</script>`.trim();

          setWidgetCode(code);

          // Prevent re-render loops
          updateFormData({
            widgetCode: code,
          });
        } catch (err) {
          console.error(err);
          setError(err.message || "Failed to load widget code.");
        } finally {
          setLoading(false);
        }
      };

      loadWidget();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.id]);

    /**
     * COPY HANDLER
     */
    const handleCopy = () => {
      navigator.clipboard.writeText(widgetCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    /**
     * LOADING UI
     */
    if (loading) {
      return (
        <Box sx={{ maxWidth: 800, mx: "auto", textAlign: "center" }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Loading Widget Code... 🚀
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Preparing your AI chat widget
          </Typography>
        </Box>
      );
    }

    /**
     * ERROR UI
     */
    if (error) {
      return (
        <Box sx={{ maxWidth: 800, mx: "auto", mt: 6 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    /**
     * SUCCESS UI
     */
    return (
      <Box sx={{ maxWidth: 800, mx: "auto" }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <CheckCircle sx={{ color: "success.main", fontSize: 48, mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Your AI Chat Is Ready! 🎉
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Copy this code and paste it before the closing{" "}
              <code>&lt;/body&gt;</code> tag on your website.
            </Typography>
          </Box>
        </Box>

        {/* CODE BOX */}
        <Card sx={{ mb: 4, border: "1px solid rgba(0, 0, 0, 0.08)" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Widget Embed Code
            </Typography>

            <Paper
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: "#1e1e1e",
                borderRadius: 2,
              }}
            >
              <TextField
                fullWidth
                value={widgetCode}
                multiline
                rows={6}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: "monospace",
                    fontSize: "12px",
                    color: "#fff",
                  },
                }}
              />
            </Paper>

            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={handleCopy}
              fullWidth
              size="large"
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                },
              }}
            >
              {copied ? "Copied!" : "Copy Code"}
            </Button>
          </CardContent>
        </Card>

        {/* INFO ALERT */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            What happens next:
          </Typography>

          <Typography variant="body2" component="div">
            • 📧 Customer emails appear in your dashboard for follow-up
            <br />
            {onboardingData?.scheduling?.schedulingEnabled && (
              <>
                • 📅 Customers can book meetings directly from chat
                <br />
              </>
            )}
            • 💬 Your AI answers questions 24/7, even when you're away
            <br />• 📊 See all conversations and leads in your dashboard
          </Typography>
        </Alert>
      </Box>
    );
  },
);

export default WidgetCodeStep;
